import { Link } from "react-router-dom";
import type { Video } from "@/types/video";
import { formatViews, typeLabels } from "@/utils/video";

interface VideoCardProps {
  video: Video;
  showSummary?: boolean;
  rank?: number;
}

export default function VideoCard({ video, showSummary = false, rank }: VideoCardProps) {
  return (
    <Link
      to={`/detail/${video.id}`}
      className="group flex flex-col overflow-hidden rounded-lg border border-background-200/80 bg-background-100 transition-all duration-300 hover:-translate-y-1 hover:border-primary-400/70"
    >
      <div className="relative aspect-video w-full overflow-hidden bg-background-200">
        <img
          src={video.cover}
          alt={`${video.title}封面`}
          title={`${video.title} - ${typeLabels[video.type]}在线观看`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50/85 via-transparent to-transparent opacity-80" />

        <span className="absolute left-2.5 top-2.5 rounded bg-background-50/80 px-1.5 py-0.5 font-label text-[10px] text-foreground-900 backdrop-blur">
          {typeLabels[video.type]}
        </span>

        <span className="absolute right-2.5 top-2.5 flex items-center gap-1 rounded bg-background-50/80 px-1.5 py-0.5 font-label text-[10px] text-primary-400 backdrop-blur">
          <i className="ri-star-fill text-[10px]" />
          {video.rating.toFixed(1)}
        </span>

        {typeof rank === "number" && (
          <span className="absolute bottom-2.5 left-2.5 flex h-7 w-7 items-center justify-center rounded bg-primary-500 font-label text-xs font-semibold text-background-50">
            {rank}
          </span>
        )}

        <span className="absolute bottom-2.5 right-2.5 rounded bg-background-50/80 px-1.5 py-0.5 font-label text-[10px] text-foreground-800 backdrop-blur">
          {video.duration}
        </span>

        <span className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-500/95 text-background-50">
            <i className="ri-play-fill text-xl" />
          </span>
        </span>
      </div>

      <div className="flex flex-1 flex-col gap-1.5 p-3 md:p-3.5">
        <h3 className="truncate font-heading text-[15px] font-medium text-foreground-900 transition-colors group-hover:text-primary-400">
          {video.title}
        </h3>
        <p className="truncate text-xs text-foreground-500">
          {video.year} · {video.region} · {video.genres.slice(0, 2).join(" / ")}
        </p>
        {showSummary && (
          <p className="mt-0.5 line-clamp-2 text-xs leading-relaxed text-foreground-600">
            {video.summary}
          </p>
        )}
        <div className="mt-auto flex items-center justify-between pt-1 text-[11px] text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-play-circle-line text-sm" />
            {formatViews(video.views)}次播放
          </span>
          <span className="flex items-center gap-1 text-foreground-500">
            <i className="ri-calendar-line text-xs" />
            {video.updatedAt}
          </span>
        </div>
      </div>
    </Link>
  );
}